import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form, Input, Button, Spin, message, Card, Select,
  Space, Table, Popconfirm, Modal, Row, Col, Tag,
} from "antd";
import { PlusOutlined, DeleteOutlined, SaveOutlined, ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { api } from "@redux/api";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { EventName, emitEvent } from "@/hooks/useEventBus";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import { useSelector } from "react-redux";

const { Option } = Select;
const { TextArea } = Input;

const FIELD_TYPES = [
  { value: "text", label: "Text Input" },
  { value: "number", label: "Number" },
  { value: "select", label: "Dropdown Select" },
  { value: "boolean", label: "Yes / No" },
  { value: "photo", label: "Photo Upload" },
  { value: "textarea", label: "Text Area" },
  { value: "date", label: "Date" },
];

const PMChecklistDetail = ({ isAdd = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshTab } = useKeepAliveContext();
  const [form] = Form.useForm();
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const listRoute = buildFacilityPath("pm-checklist", facilityContext) || "/switch-view";

  const [loading, setLoading] = useState(!isAdd);
  const [saving, setSaving] = useState(false);
  const [readOnly, setReadOnly] = useState(!isAdd);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [fieldForm] = Form.useForm();
  const [editingFieldIdx, setEditingFieldIdx] = useState(null);

  useEffect(() => {
    fetchCategories();
    if (!isAdd && id) fetchDetail();
  }, [id, isAdd]);

  const fetchCategories = async () => {
    try {
      const res = await api.filterEquipmentCategory("page=1&page_size=999");
      if (res.status === 200) setCategoryOptions(res.data.results || []);
    } catch {}
  };

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await api.getPMChecklistDetail(id);
      if (res.status === 200) {
        const data = res.data;
        form.setFieldsValue({
          name: data.name,
          description: data.description,
          equipment_category: data.equipment_category,
        });
        setFields(Array.isArray(data.fields) ? data.fields : []);
      } else {
        message.error("Checklist not found");
      }
    } catch {
      message.error("Failed to load checklist");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const body = { ...values, fields };

      let res;
      if (isAdd) {
        res = await api.createPMChecklist(body);
      } else {
        res = await api.editPMChecklist(id, body);
      }

      if (res.status === 200 || res.status === 201) {
        message.success(isAdd ? "Checklist created" : "Checklist updated");
        emitEvent(EventName.REFRESH_PM_CHECKLIST_LIST);
        refreshTab(listRoute);
        if (isAdd) navigate(buildFacilityPath(`pm-checklist-detail/${res.data.id}`, facilityContext) || listRoute);
        else { setReadOnly(true); fetchDetail(); }
      } else {
        message.error("Save failed");
      }
    } catch (err) {
      if (err.errorFields) return;
      message.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Field management
  const openAddField = () => {
    setEditingFieldIdx(null);
    fieldForm.resetFields();
    setFieldModalVisible(true);
  };

  const openEditField = (idx) => {
    setEditingFieldIdx(idx);
    const raw = fields[idx] || {};
    const forForm = { ...raw };
    delete forForm.pass_fail;
    fieldForm.setFieldsValue(forForm);
    setFieldModalVisible(true);
  };

  const handleFieldSave = async () => {
    try {
      const values = await fieldForm.validateFields();
      const newFields = [...fields];
      if (editingFieldIdx !== null) {
        const prev = fields[editingFieldIdx] || {};
        const normalized = { ...values };
        if (prev.pass_fail === true) normalized.pass_fail = true;
        newFields[editingFieldIdx] = normalized;
      } else {
        newFields.push({ ...values });
      }
      setFields(newFields);
      setFieldModalVisible(false);
    } catch {}
  };

  const handleFieldDelete = (idx) => {
    setFields(fields.filter((_, i) => i !== idx));
  };

  const fieldColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: "Field Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (v) => {
        const t = FIELD_TYPES.find((x) => x.value === v);
        return <Tag color="blue">{t ? t.label : v}</Tag>;
      },
    },
    {
      title: "Unit / Options",
      key: "extra",
      render: (_, rec) => {
        if (rec.unit) return <span>{rec.unit}</span>;
        if (rec.options) return <span>{Array.isArray(rec.options) ? rec.options.join(", ") : rec.options}</span>;
        return "-";
      },
    },
    {
      title: "Required",
      dataIndex: "required",
      key: "required",
      render: (v) => (v ? <Tag color="red">Required</Tag> : <Tag>Optional</Tag>),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, __, idx) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditField(idx)} disabled={readOnly} />
          <Popconfirm title="Delete this field?" onConfirm={() => handleFieldDelete(idx)}>
            <Button size="small" danger icon={<DeleteOutlined />} disabled={readOnly} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}><Spin size="large" /></div>;

  return (
    <div style={{ padding: "16px" }}>
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => { refreshTab(listRoute); navigate(listRoute); }} />
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {isAdd ? "Create PM Checklist" : "PM Checklist Detail"}
            </span>
          </Space>
        }
        extra={
          <Space>
            {!isAdd && readOnly && (
              <Button onClick={() => setReadOnly(false)}>Edit</Button>
            )}
            {(!readOnly || isAdd) && (
              <>
                {!isAdd && (
                  <Button onClick={() => { setReadOnly(true); fetchDetail(); }}>Cancel</Button>
                )}
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={saving}
                  onClick={handleSave}
                >
                  Save
                </Button>
              </>
            )}
          </Space>
        }
      >
        <Form form={form} layout="vertical" disabled={readOnly && !isAdd}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Checklist Name"
                name="name"
                rules={[{ required: true, message: "Please enter checklist name" }]}
              >
                <Input placeholder="e.g. HVAC Monthly PM Checklist" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Equipment Category"
                name="equipment_category"
                rules={[{ required: true, message: "Please select equipment category" }]}
              >
                <Select placeholder="Select category" showSearch optionFilterProp="children">
                  {categoryOptions.map((cat) => (
                    <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Description" name="description">
                <TextArea rows={2} placeholder="Optional description" />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {/* Checklist Fields */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>Checklist Fields ({fields.length})</span>
            {!readOnly && (
              <Button type="dashed" icon={<PlusOutlined />} onClick={openAddField}>
                Add Field
              </Button>
            )}
          </div>
          <Table
            rowKey={(_, idx) => idx}
            columns={fieldColumns}
            dataSource={fields}
            pagination={false}
            size="small"
            bordered
          />
        </div>
      </Card>

      {/* Field Editor Modal */}
      <Modal
        title={editingFieldIdx !== null ? "Edit Field" : "Add Field"}
        open={fieldModalVisible}
        onOk={handleFieldSave}
        onCancel={() => setFieldModalVisible(false)}
        destroyOnClose
      >
        <Form form={fieldForm} layout="vertical">
          <Form.Item label="Field Name" name="name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Operating Status" />
          </Form.Item>
          <Form.Item label="Field Type" name="type" rules={[{ required: true }]}>
            <Select placeholder="Select type">
              {FIELD_TYPES.map((t) => (
                <Option key={t.value} value={t.value}>{t.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Unit (for number fields)" name="unit">
            <Input placeholder="e.g. °C, PSI" />
          </Form.Item>
          <Form.Item label="Options (for select fields, comma separated)" name="options">
            <Input placeholder="e.g. Normal,Abnormal,Needs Repair" />
          </Form.Item>
          <Form.Item label="Required" name="required" initialValue={false}>
            <Select>
              <Option value={false}>Optional</Option>
              <Option value={true}>Required</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PMChecklistDetail;
